// Mirrors backend/pkg/puzzle/rules.go. Both are tested against
// backend/pkg/puzzle/testdata/scoring_cases.json, so change them together.

export const RACK_SIZE = 7
export const ALL_TILES_BONUS = 50

export const CELL = {
  blocked: -1,
  normal: 0,
  doubleLetter: 1,
  doubleWord: 2,
  tripleWord: 3,
  tripleLetter: 4,
} as const

const LETTER_VALUES = [1, 3, 3, 2, 1, 4, 2, 4, 1, 8, 5, 1, 3, 1, 1, 3, 10, 1, 1, 1, 1, 4, 4, 8, 4, 10]

export function letterValue(letter: string): number {
  const i = letter.charCodeAt(0) - 65
  return i >= 0 && i < 26 ? LETTER_VALUES[i] : 0
}

export interface Tile {
  row: number
  col: number
  letter: string
}

export interface Play {
  tiles: Tile[]
  words: string[]
  score: number
}

export interface PlacedWord {
  word: string
  row: number
  col: number
  direction: 'horizontal' | 'vertical'
}

export interface Puzzle {
  date: string
  board_layout: number[][]
  pre_placed_words: PlacedWord[]
  player_letters: string
  board_width: number
  board_height: number
  best_play?: Play
  play_count?: number
  /** Every legal play tallied by score, highest first. */
  play_scores?: { score: number; count: number }[]
}

export type RuleErrorCode =
  | 'no_tiles'
  | 'not_in_rack'
  | 'off_board'
  | 'blocked'
  | 'occupied'
  | 'not_in_line'
  | 'gap'
  | 'not_connected'
  | 'invalid_word'

export type Evaluation =
  | { ok: true; play: Play }
  /** For invalid_word: `word` is the first word not in the list (as the Go engine reports it) and `words` all of them. */
  | { ok: false; code: RuleErrorCode; word?: string; words?: string[] }

/** Pre-placed letters indexed [row][col]; '' is empty. */
export function boardLetters(p: Puzzle): string[][] {
  const grid = Array.from({ length: p.board_height }, () => Array<string>(p.board_width).fill(''))
  for (const w of p.pre_placed_words) {
    const [dr, dc] = w.direction === 'vertical' ? [1, 0] : [0, 1]
    for (let i = 0; i < w.word.length; i++) grid[w.row + dr * i][w.col + dc * i] = w.word[i]
  }
  return grid
}

export function evaluate(p: Puzzle, tiles: Tile[], isWord: (w: string) => boolean): Evaluation {
  if (tiles.length === 0) return { ok: false, code: 'no_tiles' }

  const board = boardLetters(p)
  const rack = new Map<string, number>()
  for (const ch of p.player_letters) rack.set(ch, (rack.get(ch) ?? 0) + 1)

  const placed = new Map<string, string>()
  const key = (r: number, c: number) => `${r},${c}`
  for (const t of tiles) {
    if (!/^[A-Z]$/.test(t.letter)) return { ok: false, code: 'not_in_rack' }
    const left = (rack.get(t.letter) ?? 0) - 1
    if (left < 0) return { ok: false, code: 'not_in_rack' }
    rack.set(t.letter, left)
    if (t.row < 0 || t.row >= p.board_height || t.col < 0 || t.col >= p.board_width) {
      return { ok: false, code: 'off_board' }
    }
    if (p.board_layout[t.row][t.col] === CELL.blocked) return { ok: false, code: 'blocked' }
    if (board[t.row][t.col] || placed.has(key(t.row, t.col))) return { ok: false, code: 'occupied' }
    placed.set(key(t.row, t.col), t.letter)
  }

  const letterAt = (r: number, c: number): string => {
    if (r < 0 || r >= p.board_height || c < 0 || c >= p.board_width) return ''
    return placed.get(key(r, c)) ?? board[r][c]
  }

  const sameRow = tiles.every((t) => t.row === tiles[0].row)
  const sameCol = tiles.every((t) => t.col === tiles[0].col)
  if (!sameRow && !sameCol) return { ok: false, code: 'not_in_line' }
  const [dr, dc] = sameRow ? [0, 1] : [1, 0]

  const pos = (t: Tile) => t.row * dr + t.col * dc
  let first = tiles[0]
  let last = tiles[0]
  for (const t of tiles) {
    if (pos(t) < pos(first)) first = t
    if (pos(t) > pos(last)) last = t
  }
  for (let r = first.row, c = first.col; r !== last.row || c !== last.col; r += dr, c += dc) {
    if (!letterAt(r, c)) return { ok: false, code: 'gap' }
  }

  const wordThrough = (r: number, c: number, dr: number, dc: number) => {
    while (letterAt(r - dr, c - dc)) {
      r -= dr
      c -= dc
    }
    const out = { word: '', points: 0, mult: 1, touchesBoard: false }
    for (; letterAt(r, c); r += dr, c += dc) {
      const ch = letterAt(r, c)
      let v = letterValue(ch)
      if (placed.has(key(r, c))) {
        const cell = p.board_layout[r][c]
        if (cell === CELL.doubleLetter) v *= 2
        else if (cell === CELL.tripleLetter) v *= 3
        else if (cell === CELL.doubleWord) out.mult *= 2
        else if (cell === CELL.tripleWord) out.mult *= 3
      } else {
        out.touchesBoard = true
      }
      out.word += ch
      out.points += v
    }
    return out
  }

  const formed = []
  const main = wordThrough(first.row, first.col, dr, dc)
  if (main.word.length > 1) formed.push(main)
  for (const t of tiles) {
    const cross = wordThrough(t.row, t.col, dc, dr)
    if (cross.word.length > 1) formed.push(cross)
  }

  if (!formed.some((w) => w.touchesBoard)) return { ok: false, code: 'not_connected' }

  const invalid = [...new Set(formed.map((w) => w.word).filter((w) => !isWord(w)))]
  if (invalid.length) return { ok: false, code: 'invalid_word', word: invalid[0], words: invalid }

  const play: Play = { tiles, words: [], score: 0 }
  for (const w of formed) {
    play.words.push(w.word)
    play.score += w.points * w.mult
  }
  if (tiles.length === RACK_SIZE) play.score += ALL_TILES_BONUS
  return { ok: true, play }
}

const MESSAGES: Record<RuleErrorCode, string> = {
  no_tiles: 'Place some tiles first.',
  not_in_rack: 'You can only use your own letters.',
  off_board: 'That square is off the board.',
  blocked: 'That square is blocked.',
  occupied: 'That square is already taken.',
  not_in_line: 'Tiles must be in a single row or column.',
  gap: 'Tiles must form one continuous word with no gaps.',
  not_connected: 'Your word must connect to a word on the board.',
  invalid_word: 'is not in the word list.',
}

export function describeError(e: { code: RuleErrorCode; word?: string; words?: string[] }): string {
  const words = e.words ?? (e.word ? [e.word] : [])
  if (!words.length) return MESSAGES[e.code]
  if (words.length === 1) return `${words[0]} ${MESSAGES[e.code]}`
  // "AB, CD and EF are not in the word list."
  const list = `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`
  return `${list} ${MESSAGES[e.code].replace(/^is /, 'are ')}`
}
